import {
  markerRulerLane,
  tokenizeRppLine,
} from "../../../preview/ma/parseRppProject";
import type {
  MigrationPlan,
  PlannedItem,
  PlannedLane,
  PlannedMarker,
  PlannedTrack,
} from "./migrationPlan";

/**
 * 移植プランを既存の RPP テキストへ差し込む。
 *
 * 既存の内容は書き換えない（名前なし・未使用のルーラーレーンに名前を付ける
 * ことだけは行う）。差し込み位置は REAPER 自身の書き出し順に合わせる:
 * - RULERLANE: 既存の RULERLANE の後ろ（無ければマーカーと同じ位置）
 * - MARKER: 既存の MARKER の後ろ（無ければ PROJBAY / 最初の TRACK の直前）
 * - TRACK: 既存トラックより上（最初の TRACK の直前）。REAPER のビデオ
 *   ウィンドウは上のトラックを優先表示するため、移植した動画が前面になる
 *
 * 行の書式は REAPER 7.84 の書き出しに揃えている。
 */

export interface RppWriteOptions {
  /** `{XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX}` を返す（テスト用に差し替え可能） */
  createGuid?: () => string;
}

type CreateGuid = () => string;

const INDENT = "  ";

/** トラック既定色（REAPER が新規トラックに書く PEAKCOL） */
const DEFAULT_PEAKCOL = 16576;

const createRppGuid: CreateGuid = () => {
  const uuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = Math.floor(Math.random() * 16);
          return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
        });
  return `{${uuid.toUpperCase()}}`;
};

/**
 * RPP の文字列トークン。REAPER は値に含まれない囲み文字（" → ' → `）を選んで
 * 囲む（エスケープ記法はない）。3 種とも含む場合は " を ' に置き換える
 */
export function quoteRppString(value: string): string {
  const v = value.replace(/[\r\n]+/g, " ");
  if (!v.includes('"')) return `"${v}"`;
  if (!v.includes("'")) return `'${v}'`;
  if (!v.includes("`")) return `\`${v}\``;
  return `"${v.replace(/"/g, "'")}"`;
}

/** 秒数を RPP の数値表記に（小数 10 桁まで、末尾の 0 を落とす。指数表記にしない） */
export function formatRppNumber(value: number): string {
  if (!Number.isFinite(value)) return "0";
  const trimmed = value.toFixed(10).replace(/\.?0+$/, "");
  return trimmed === "-0" || trimmed === "" ? "0" : trimmed;
}

const indentLines = (lines: string[], depth: number): string[] =>
  lines.map((line) => INDENT.repeat(depth) + line);

// ===== チャンク生成 =====

function itemChunk(item: PlannedItem, guid: CreateGuid): string[] {
  const file = `${INDENT}FILE ${quoteRppString(item.filePath)}`;
  // 動画の音声を切る（映像は表示したまま）。REAPER のソース設定と同じ書式
  const source =
    item.sourceType === "VIDEO" && item.muteVideoAudio
      ? ["<SOURCE VIDEO", `${INDENT}AUDIO 0`, file, ">"]
      : [`<SOURCE ${item.sourceType}`, file, ">"];
  return [
    "<ITEM",
    ...indentLines(
      [
        `POSITION ${formatRppNumber(item.position)}`,
        "SNAPOFFS 0",
        `LENGTH ${formatRppNumber(item.length)}`,
        "LOOP 0",
        "ALLTAKES 0",
        "FADEIN 1 0 0 1 0 0 0 1 0 0 1 0 0",
        "FADEOUT 1 0 0 1 0 0 0 1 0 0 1 0 0",
        "MUTE 0 0",
        "SEL 0",
        `IGUID ${guid()}`,
        `NAME ${quoteRppString(item.name)}`,
        "VOLPAN 1 0 1 -1",
        `SOFFS ${formatRppNumber(item.sourceOffset)}`,
        "PLAYRATE 1 1 0 -1 0 0.0025",
        "CHANMODE 0",
        `GUID ${guid()}`,
        ...source,
      ],
      1,
    ),
    ">",
  ];
}

function trackChunk(
  track: PlannedTrack,
  trackGuid: string,
  guid: CreateGuid,
): string[] {
  return [
    `<TRACK ${trackGuid}`,
    ...indentLines(
      [
        `NAME ${quoteRppString(track.name)}`,
        `PEAKCOL ${track.color ?? DEFAULT_PEAKCOL}`,
        "BEAT -1",
        "AUTOMODE 0",
        "VOLPAN 1 0 -1 -1 1",
        "MUTESOLO 0 0 0",
        "IPHASE 0",
        "ISBUS 0 0",
        "SHOWINMIX 1 0.6667 0.5 1 0.5 0 0 0 0",
        "SEL 0",
        "REC 0 0 1 0 0 0 0 0",
        "TRACKHEIGHT 0 0 0 0 0 0 0",
        "NCHAN 2",
        "FX 1",
        `TRACKID ${trackGuid}`,
        "MAINSEND 1 0",
        ...track.items.flatMap((item) => itemChunk(item, guid)),
      ],
      1,
    ),
    ">",
  ];
}

// ===== 既存 RPP の構造 =====

interface RootLine {
  index: number;
  tokens: string[];
}

/** プロジェクト直下（深さ 1）の行とチャンク位置 */
interface RppLayout {
  rootEnd: number;
  rulerLanes: RootLine[];
  markers: RootLine[];
  /** 深さ 1 のチャンク開始行（タグ → 最初の出現行） */
  firstChunk: Map<string, number>;
}

function scanLayout(lines: string[]): RppLayout {
  let depth = 0;
  let rootStart = -1;
  let rootEnd = -1;
  const rulerLanes: RootLine[] = [];
  const markers: RootLine[] = [];
  const firstChunk = new Map<string, number>();

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("<")) {
      const tag = tokenizeRppLine(trimmed.slice(1))[0] ?? "";
      if (depth === 0 && rootStart < 0 && tag === "REAPER_PROJECT")
        rootStart = i;
      if (depth === 1 && !firstChunk.has(tag)) firstChunk.set(tag, i);
      depth++;
      continue;
    }
    if (trimmed === ">") {
      depth--;
      if (depth === 0 && rootStart >= 0) {
        rootEnd = i;
        break;
      }
      continue;
    }
    if (depth !== 1) continue;
    const tokens = tokenizeRppLine(trimmed);
    if (tokens[0] === "RULERLANE") rulerLanes.push({ index: i, tokens });
    else if (tokens[0] === "MARKER") markers.push({ index: i, tokens });
  }

  if (rootStart < 0 || rootEnd < 0) {
    throw new Error(
      "REAPER のプロジェクトファイル（.RPP）として読み込めません",
    );
  }
  return { rootEnd, rulerLanes, markers, firstChunk };
}

// ===== 差し込む行の組み立て =====

/**
 * プランのレーンを RULERLANE に割り当てる。同名の既存レーンを再利用 →
 * 名前なし・未使用の既存レーンに命名（lines を直接書き換える）→ 末尾に追加、の順
 */
function assignRulerLanes(
  lines: string[],
  layout: RppLayout,
  lanes: PlannedLane[],
): { laneIndexByName: Map<string, number>; newLaneLines: string[] } {
  const usedLanes = new Set(
    layout.markers
      .map((m) => markerRulerLane(m.tokens))
      .filter((l): l is number => l !== null),
  );
  const existingLanes = layout.rulerLanes.map((line) => ({
    line,
    laneIndex: parseInt(line.tokens[1] ?? "", 10),
    name: line.tokens[3] ?? "",
  }));
  const laneIndexByName = new Map<string, number>();
  const claimed = new Set<number>();
  const newLaneLines: string[] = [];
  let nextLaneIndex =
    Math.max(
      0,
      ...existingLanes.map((l) => l.laneIndex).filter(Number.isFinite),
    ) + 1;

  lanes.forEach((lane) => {
    const sameName = existingLanes.find(
      (l) => l.name.toLowerCase() === lane.name.toLowerCase(),
    );
    if (sameName) {
      laneIndexByName.set(lane.name, sameName.laneIndex);
      return;
    }
    const unnamed = existingLanes.find(
      (l) =>
        !l.name && !usedLanes.has(l.laneIndex) && !claimed.has(l.laneIndex),
    );
    if (unnamed) {
      claimed.add(unnamed.laneIndex);
      laneIndexByName.set(lane.name, unnamed.laneIndex);
      // [RULERLANE, 番号, flags, 名前, 色, ...]。flags（既定レーン指定）は保つ
      const tokens = [...unnamed.line.tokens];
      tokens[3] = quoteRppString(lane.name);
      tokens[4] = String(lane.color);
      const indent = lines[unnamed.line.index].match(/^\s*/)?.[0] ?? INDENT;
      lines[unnamed.line.index] = indent + tokens.join(" ");
      return;
    }
    laneIndexByName.set(lane.name, nextLaneIndex);
    newLaneLines.push(
      `RULERLANE ${nextLaneIndex} 0 ${quoteRppString(lane.name)} ${lane.color} -1 0`,
    );
    nextLaneIndex += 1;
  });

  return { laneIndexByName, newLaneLines };
}

/**
 * MARKER 行（リージョンは開始行・REGIONRENDER・終端行）を組み立てる。
 * 番号はリージョン / マーカーそれぞれ既存の最大値の続きから振る
 */
function buildMarkerLines(
  layout: RppLayout,
  markers: PlannedMarker[],
  laneIndexByName: Map<string, number>,
  trackGuidByKey: Map<string, string>,
  guid: CreateGuid,
): string[] {
  let nextRegionId = 1;
  let nextMarkerId = 1;
  layout.markers.forEach(({ tokens }) => {
    const id = parseInt(tokens[1] ?? "", 10);
    if (!Number.isFinite(id)) return;
    const isRegion = ((parseInt(tokens[4] ?? "0", 10) || 0) & 1) === 1;
    if (isRegion) nextRegionId = Math.max(nextRegionId, id + 1);
    else nextMarkerId = Math.max(nextMarkerId, id + 1);
  });

  // [MARKER, 番号, 位置, 名前, flags(bit0 = リージョン), 色, 1, B, GUID, 0, レーン]
  const startLine = (id: number, marker: PlannedMarker, flags: 0 | 1) => {
    const lane = laneIndexByName.get(marker.laneName);
    return [
      `MARKER ${id} ${formatRppNumber(marker.start)} ${quoteRppString(marker.name)}`,
      `${flags} ${marker.color} 1 B ${guid()} 0`,
      ...(lane !== undefined ? [String(lane)] : []),
    ].join(" ");
  };

  return markers.flatMap((marker) => {
    if (marker.end === null) return [startLine(nextMarkerId++, marker, 0)];
    const id = nextRegionId++;
    const renderGuid = marker.renderTrackKey
      ? trackGuidByKey.get(marker.renderTrackKey)
      : undefined;
    return [
      startLine(id, marker, 1),
      // REAPER はレンダー対象トラックをリージョン開始行の直後に書く
      ...(renderGuid
        ? [`<REGIONRENDER ${id}`, `${INDENT}TRACK ${renderGuid} 0`, ">"]
        : []),
      `MARKER ${id} ${formatRppNumber(marker.end)} "" 1`,
    ];
  });
}

/** レーン / マーカー / トラックそれぞれの差し込み位置（この行の直前に入れる） */
function insertionPoints(layout: RppLayout) {
  const { rootEnd, rulerLanes, markers, firstChunk } = layout;
  const lastLaneNext = rulerLanes.length
    ? rulerLanes[rulerLanes.length - 1].index + 1
    : undefined;
  const marker = markers.length
    ? markers[markers.length - 1].index + 1
    : (lastLaneNext ??
      firstChunk.get("PROJBAY") ??
      firstChunk.get("TRACK") ??
      firstChunk.get("EXTENSIONS") ??
      rootEnd);
  return {
    lane: lastLaneNext ?? markers[0]?.index ?? marker,
    marker,
    track: firstChunk.get("TRACK") ?? firstChunk.get("EXTENSIONS") ?? rootEnd,
  };
}

// ===== 本体 =====

export function mergeIntoRpp(
  rppText: string,
  plan: MigrationPlan,
  options: RppWriteOptions = {},
): string {
  const guid = options.createGuid ?? createRppGuid;
  const bom = rppText.startsWith("\uFEFF") ? "\uFEFF" : "";
  const body = bom ? rppText.slice(1) : rppText;
  const eol = body.includes("\r\n") ? "\r\n" : "\n";
  const lines = body.split(/\r\n|\n/);
  const layout = scanLayout(lines);

  const { laneIndexByName, newLaneLines } = assignRulerLanes(
    lines,
    layout,
    plan.lanes,
  );
  // トラックの GUID を先に決め、リージョンのレンダー対象から参照する
  const trackGuidByKey = new Map(
    plan.tracks.map((track) => [track.key, guid()]),
  );
  const markerLines = buildMarkerLines(
    layout,
    plan.markers,
    laneIndexByName,
    trackGuidByKey,
    guid,
  );
  const trackLines = plan.tracks.flatMap((track) =>
    trackChunk(track, trackGuidByKey.get(track.key)!, guid),
  );

  // 同じ位置に入る場合はレーン → マーカー → トラックの順に並ぶ
  const points = insertionPoints(layout);
  const insertBefore = new Map<number, string[]>();
  (
    [
      [points.lane, newLaneLines],
      [points.marker, markerLines],
      [points.track, trackLines],
    ] as const
  ).forEach(([index, newLines]) => {
    if (newLines.length === 0) return;
    insertBefore.set(index, [
      ...(insertBefore.get(index) ?? []),
      ...indentLines(newLines, 1),
    ]);
  });

  const output = lines.flatMap((line, index) => [
    ...(insertBefore.get(index) ?? []),
    line,
  ]);
  return bom + output.join(eol);
}
